import dayjs from 'dayjs';
import { Dispatch } from 'redux';
import { isDfdAuthenticatedSelector } from 'store/authentication/selectors';
import { Thunk, RootState } from 'store/types';
import { TriagePatient, TriageProxy } from 'store/triageGyant/types';
import { RESET_E_VISIT_SESSION_TOKEN } from 'store/eVisit/actions';
import { getDispatcherUrl } from 'modules/utils/UrlUtils';
import {
  DispatcherCategoryService,
  DispatcherAuthPrefix,
  DispatcherResource,
  DispatcherEndpoint
} from 'modules/types/common';
import { getGuestEndpoint, getSymptomCheckerService } from './utils';

// Results:
export const TRIAGE_GYANT_RESULTS = 'TRIAGE.TRIAGE_GYANT_RESULTS';
export const TRIAGE_GYANT_RESULTS_SUCCESS = 'TRIAGE.TRIAGE_GYANT_RESULTS_SUCCESS';
export const TRIAGE_GYANT_RESULTS_FAIL = 'TRIAGE.TRIAGE_GYANT_RESULTS_FAIL';
export const getTriageResults = (caseId: string): Thunk => async (
  dispatch: Dispatch,
  getState: () => RootState
) => {
  const state = getState();
  const auth = isDfdAuthenticatedSelector(state);
  const CASES_ID_URL = getDispatcherUrl(
    DispatcherCategoryService.DELIVER_CARE,
    auth ? DispatcherAuthPrefix.AUTHENTICATED : DispatcherAuthPrefix.PUBLIC,
    DispatcherResource.SYMPTOM_CHECKER,
    DispatcherEndpoint.CASES_ID
  );
  const { SC_EXPANSION_ENABLED, client, url } = getSymptomCheckerService(
    `${getGuestEndpoint()}/patients/triage-results/${caseId}`,
    CASES_ID_URL
  );
  const headers = {
    'Content-Type': 'application/json'
  };
  if (SC_EXPANSION_ENABLED) {
    headers['mhp-adapter'] = 'mhp';
    headers['mhp-filter-resource-id'] = caseId;
  }

  dispatch({
    type: TRIAGE_GYANT_RESULTS,
    payload: {
      client,
      request: {
        method: 'get',
        url,
        headers
      }
    }
  });
};

export const TRIAGE_GYANT_CONDITION_DETAIL = 'TRIAGE.TRIAGE_GYANT_CONDITION_DETAIL';
export const TRIAGE_GYANT_CONDITION_DETAIL_SUCCESS = 'TRIAGE.TRIAGE_GYANT_CONDITION_DETAIL_SUCCESS';
export const TRIAGE_GYANT_CONDITION_DETAIL_FAIL = 'TRIAGE.TRIAGE_GYANT_CONDITION_DETAIL_FAIL';
export const getConditionDetail = (conditionId: string): Thunk => async (
  dispatch: Dispatch,
  getState: () => RootState
) => {
  const state = getState();
  const auth = isDfdAuthenticatedSelector(state);
  const CONDITIONS_URL = getDispatcherUrl(
    DispatcherCategoryService.DELIVER_CARE,
    auth ? DispatcherAuthPrefix.AUTHENTICATED : DispatcherAuthPrefix.PUBLIC,
    DispatcherResource.SYMPTOM_CHECKER,
    DispatcherEndpoint.CONDITIONS
  );
  const { SC_EXPANSION_ENABLED, client, url } = getSymptomCheckerService(
    `${getGuestEndpoint()}/patients/condition-detail/${conditionId}`,
    CONDITIONS_URL
  );
  const headers = {
    'Content-Type': 'application/json'
  };
  if (SC_EXPANSION_ENABLED) {
    headers['mhp-adapter'] = 'mhp';
    headers['mhp-filter-resource-id'] = conditionId;
  }

  dispatch({
    type: TRIAGE_GYANT_CONDITION_DETAIL,
    payload: {
      client,
      request: {
        method: 'get',
        url,
        headers
      }
    }
  });
};

// Set origin for returnBackTo:
export const SET_TRIAGE_ORIGIN = 'TRIAGE.SET_TRIAGE_ORIGIN';
export const setTriageOrigin = (routeName: string | null) => ({
  type: SET_TRIAGE_ORIGIN,
  payload: routeName
});

// Chat reset:
export const RESET_TRIAGE_CHAT_BOOLEAN_SWITCH = 'TRIAGE.RESET_TRIAGE_CHAT_BOOLEAN_SWITCH';
export const resetTriageChat = () => ({
  type: RESET_TRIAGE_CHAT_BOOLEAN_SWITCH
});
export const UNSET_TRIAGE_CHAT_BOOLEAN_SWITCH = 'TRIAGE.UNSET_TRIAGE_CHAT_BOOLEAN_SWITCH';
export const unsetResetChat = () => ({
  type: UNSET_TRIAGE_CHAT_BOOLEAN_SWITCH
});

// Clear data:
export const RESET_TRIAGE_CHAT_DATA = 'TRIAGE.RESET_TRIAGE_CHAT_DATA';
export const resetTriageChatData = () => ({
  type: RESET_TRIAGE_CHAT_DATA
});
export const RESET_TRIAGE_RESULTS_DATA = 'TRIAGE.RESET_TRIAGE_RESULTS_DATA';
export const resetTriageResultsData = () => ({
  type: RESET_TRIAGE_RESULTS_DATA
});
export const resetTriageData = (): Thunk => async dispatch => {
  dispatch({ type: RESET_TRIAGE_CHAT_DATA });
  dispatch({ type: RESET_TRIAGE_RESULTS_DATA });
  dispatch({ type: RESET_E_VISIT_SESSION_TOKEN });
};

// Manage sessions:
export const TRIAGE_GYANT_LAST_SESSION_TOKEN = 'TRIAGE.TRIAGE_GYANT_LAST_SESSION_TOKEN';
export const TRIAGE_GYANT_LAST_SESSION_TOKEN_SUCCESS =
  'TRIAGE.TRIAGE_GYANT_LAST_SESSION_TOKEN_SUCCESS';
export const TRIAGE_GYANT_LAST_SESSION_TOKEN_FAIL = 'TRIAGE.TRIAGE_GYANT_LAST_SESSION_TOKEN_FAIL';
export const getLastSessionToken = (patientId: string): Thunk => async dispatch => {
  const SESSIONS_URL = getDispatcherUrl(
    DispatcherCategoryService.DELIVER_CARE,
    DispatcherAuthPrefix.AUTHENTICATED,
    DispatcherResource.SYMPTOM_CHECKER,
    DispatcherEndpoint.SESSIONS
  );
  const { SC_EXPANSION_ENABLED, client, url } = getSymptomCheckerService(
    '/patients/last-session',
    SESSIONS_URL
  );
  const headers = { 'x-gyant-patient-id': patientId };
  if (SC_EXPANSION_ENABLED) {
    headers['mhp-adapter'] = 'mhp';
  }

  dispatch({
    type: TRIAGE_GYANT_LAST_SESSION_TOKEN,
    payload: {
      client,
      request: {
        method: 'get',
        url,
        headers
      }
    }
  });
};

export const TRIAGE_GYANT_SESSION_TOKEN = 'TRIAGE.TRIAGE_GYANT_SESSION_TOKEN';
export const TRIAGE_GYANT_SESSION_TOKEN_SUCCESS = 'TRIAGE.TRIAGE_GYANT_SESSION_TOKEN_SUCCESS';
export const TRIAGE_GYANT_SESSION_TOKEN_FAIL = 'TRIAGE.TRIAGE_GYANT_SESSION_TOKEN_FAIL';
export const getSessionToken = (patientInfo: TriagePatient | TriageProxy): Thunk => async (
  dispatch: Dispatch,
  getState: () => RootState
) => {
  const state = getState();
  const auth = isDfdAuthenticatedSelector(state);
  const SESSION_URL = getDispatcherUrl(
    DispatcherCategoryService.DELIVER_CARE,
    auth ? DispatcherAuthPrefix.AUTHENTICATED : DispatcherAuthPrefix.PUBLIC,
    DispatcherResource.SYMPTOM_CHECKER,
    DispatcherEndpoint.SESSION
  );
  const { SC_EXPANSION_ENABLED, client, url } = getSymptomCheckerService(
    `${getGuestEndpoint()}/patients/session`,
    SESSION_URL
  );
  const headers = {
    'Content-Type': 'application/json'
  };
  if (SC_EXPANSION_ENABLED) {
    headers['mhp-adapter'] = 'mhp';
  } else {
    headers['x-gyant-patient-id'] = patientInfo.grantor?.patientId || patientInfo.patientId;
  }

  return dispatch({
    type: TRIAGE_GYANT_SESSION_TOKEN,
    payload: {
      client,
      request: {
        method: 'post',
        url,
        data: { ...patientInfo, timezone: dayjs().format('Z') },
        headers
      }
    }
  });
};
